/**
 * Timeout Utility
 *
 * Provides helpers for adding timeouts to async operations.
 */

/**
 * Wraps a promise with a timeout.
 * If the promise doesn't resolve within the specified time, it rejects with a timeout error.
 *
 * @param promise - The promise to wrap
 * @param timeoutMs - Timeout in milliseconds
 * @param errorMessage - Error message to use if timeout occurs
 * @returns The promise result or throws a timeout error
 *
 * @example
 * ```typescript
 * // Basic usage
 * const result = await withTimeout(
 *   fetch('/api/data'),
 *   5000,
 *   'Request timed out'
 * );
 *
 * // With Convex operations
 * const url = await withTimeout(
 *   client.mutation(api.userMedia.generateUploadUrl),
 *   30000,
 *   'Timed out generating upload URL'
 * );
 * ```
 */
export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorMessage: string
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(errorMessage)), timeoutMs)
    ),
  ]);
}

/**
 * Creates a timeout promise that rejects after the specified time.
 * Useful for Promise.race() patterns.
 *
 * @param timeoutMs - Timeout in milliseconds
 * @param errorMessage - Error message to use
 * @returns A promise that rejects after the timeout
 */
export function createTimeoutPromise(
  timeoutMs: number,
  errorMessage: string
): Promise<never> {
  return new Promise((_, reject) =>
    setTimeout(() => reject(new Error(errorMessage)), timeoutMs)
  );
}

/**
 * Default timeout values for different operation types
 */
export const TIMEOUT_DEFAULTS = {
  /** Quick operations like queries (10 seconds) */
  QUERY: 10000,
  /** Mutations that modify data (30 seconds) */
  MUTATION: 30000,
  /** File upload operations (5 minutes) */
  UPLOAD: 300000,
  /** Network requests (30 seconds) */
  NETWORK: 30000,
} as const;
